/**
 * EVO — filial ko'rsatkichi va almashtirgichi.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN BU KERAK                                                      ║
 * ║                                                                          ║
 * ║ Egasi: «organizatsiya yoki filial almashadigan bo'ldi — admin yoki       ║
 * ║ rahbar uchun filialga o'zini nomi yozilib tursin».                       ║
 * ║                                                                          ║
 * ║ Filiallar ochilgunicha savol yo'q edi: bitta MARKAZ bor edi va uni       ║
 * ║ yozishning ma'nosi ham yo'q edi. Endi beshta filial bor va ekranda       ║
 * ║ turgan raqam QAYSI filialga tegishli ekani ko'rinmasa — hisobot          ║
 * ║ o'qilmaydi.                                                              ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ BU KO'ZOYNAK, RUXSAT EMAS                                                │
 * │                                                                          │
 * │ Tanlangan filial — EKRANNI toraytiruvchi filtr, xavfsizlik chegarasi     │
 * │ EMAS. Chegara serverda (`scopedWhere`) va u foydalanuvchining ko'lamiga  │
 * │ tayanadi: filialga biriktirilgan xodim boshqa filialni tanlay olmaydi,   │
 * │ chunki `GET /org/branches` unga bitta qator qaytaradi.                   │
 * │                                                                          │
 * │ Ya'ni bu yerda tekshiradigan narsa yo'q: ro'yxatning O'ZI allaqachon     │
 * │ ruxsat natijasi. Bu muhim farq — brauzerdagi tanlovga tayangan har       │
 * │ qanday himoya soxta himoya bo'lardi.                                     │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ TANLOV EKRANGA QAYERDA YETIB BORADI                                      │
 * │                                                                          │
 * │ Bu fayl faqat TANLOVNI saqlaydi va ko'rsatadi. Uni so'rovga aylantirish  │
 * │ — `components/branch-filter.tsx` ning ishi: `useBranchFilter()`          │
 * │ tanlovni ro'yxat parametriga aylantiradi, `<BranchFilterNotice/>` esa    │
 * │ filtr yoqilganini ekranda AYTADI va o'chirish tugmasini beradi.          │
 * │                                                                          │
 * │ ⚠️ Tanlov FAQAT server `branchId` ni qabul qiladigan ro'yxatlarga        │
 * │ qo'llanadi (bugun: `GET /api/expenses`). Kelgan qatorlarni brauzerda     │
 * │ filtrlash TAQIQLANADI: sahifani server kesadi va qatorni brauzerda       │
 * │ tashlash sahifa hisoblagichini yolg'onchi qilardi.                       │
 * └──────────────────────────────────────────────────────────────────────────┘
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ TANLOV `localStorage` DA SAQLANADI                                       │
 * │                                                                          │
 * │ Rahbar ertalab «Farg'ona» ni tanlab, kun davomida sahifalar bo'ylab      │
 * │ yuradi. Har sahifada tanlovni qaytadan qilish — kunlik ish emas, azob.   │
 * │                                                                          │
 * │ ⚠️ Chiqishda TOZALANADI (`clearActiveBranch()`): bitta kompyuterda       │
 * │ navbatma-navbat ishlaydigan ikki xodimda birinchisining tanlovi          │
 * │ ikkinchisiga qolib ketardi va u boshqa filialning raqamlarini ko'rib     │
 * │ o'z filiali deb o'ylardi.                                                │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchBranches, orgKeys, type Branch } from '../../lib/org';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n-context';

const STORAGE_KEY = 'evo.activeBranchId';

export function clearActiveBranch(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* xotira yopiq — muhim emas */
  }
}

interface ActiveBranchValue {
  /** `null` — «Barcha filiallar» (faqat to'liq ko'lamli foydalanuvchida). */
  readonly branchId: number | null;
  readonly branch: Branch | null;
  readonly branches: readonly Branch[];
  readonly canSwitch: boolean;
  readonly setBranchId: (id: number | null) => void;
}

const ActiveBranchContext = createContext<ActiveBranchValue>({
  branchId: null,
  branch: null,
  branches: [],
  canSwitch: false,
  setBranchId: () => {},
});

export function useActiveBranch(): ActiveBranchValue {
  return useContext(ActiveBranchContext);
}

export function ActiveBranchProvider({ children }: { readonly children: ReactNode }): ReactNode {
  const { user } = useAuth();

  const query = useQuery({
    queryKey: orgKeys.branches,
    queryFn: ({ signal }) => fetchBranches(signal),
    enabled: user !== null,
    /*
     * Filiallar ro'yxati kun davomida o'zgarmaydi — yangi filial ochish
     * oyda bir marta bo'ladigan ish. Har sahifada qayta so'rash bekorga
     * so'rov va u mobil internetda seziladi.
     */
    staleTime: 10 * 60_000,
  });

  const branches = query.data ?? [];

  const [branchId, setBranchIdState] = useState<number | null>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw === null ? null : Number(raw);
    } catch {
      return null;
    }
  });

  /*
   * Saqlangan tanlov RO'YXATDA BOR-YO'QLIGI tekshiriladi.
   *
   * Filial arxivlansa yoki xodim boshqa filialga ko'chirilsa, eski `id`
   * `localStorage` da qolib ketadi va ekran BO'SH chiqardi — sababi esa
   * hech qayerda ko'rinmasdi. Endi bunday tanlov jimgina «barcha
   * filiallar» ga qaytadi.
   */
  useEffect(() => {
    if (branchId === null || branches.length === 0) return;
    if (!branches.some((b) => b.id === branchId)) {
      setBranchIdState(null);
      clearActiveBranch();
    }
  }, [branchId, branches]);

  const setBranchId = (id: number | null): void => {
    setBranchIdState(id);
    try {
      if (id === null) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      /* xotira yopiq — tanlov shu sessiyada ishlaydi */
    }
  };

  const value = useMemo<ActiveBranchValue>(() => {
    /*
     * Bitta filial — tanlov YO'Q, faqat nom.
     *
     * Filialga biriktirilgan xodimga «Farg'ona ▾» ko'rinishidagi ochiluvchi
     * ro'yxat ko'rsatish — ochsa ichida bitta qator turadigan tugma. U
     * foydalanuvchini «boshqasi ham bormi?» deb o'ylashga majbur qiladi va
     * javob har safar «yo'q» bo'ladi.
     */
    const canSwitch = branches.length > 1;
    const effectiveId = canSwitch ? branchId : (branches[0]?.id ?? null);
    return {
      branchId: effectiveId,
      branch: branches.find((b) => b.id === effectiveId) ?? null,
      branches,
      canSwitch,
      setBranchId,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, branchId]);

  return <ActiveBranchContext.Provider value={value}>{children}</ActiveBranchContext.Provider>;
}

/**
 * Yuqori paneldagi ko'rsatkich.
 *
 * Bitta filialli foydalanuvchida — oddiy yorliq. Bir nechta bo'lsa —
 * tanlov. Umuman bo'lmasa (ma'lumot hali kelmagan) — hech narsa
 * chizilmaydi: bo'sh tanlov qutisi «filial yo'q» degan yolg'on xabar
 * berardi.
 */
export function BranchIndicator(): ReactNode {
  const t = useT();
  const { branchId, branch, branches, canSwitch, setBranchId } = useActiveBranch();

  if (branches.length === 0) return null;

  /*
   * ┌────────────────────────────────────────────────────────────────────────┐
   * │ TELEFONDA YASHIRINADI — `md` DAN KO'RINADI                             │
   * │                                                                        │
   * │ Filial nomi 120–200 px oladi. 390 px li ekranda bu kunlik kursni       │
   * │ pastki qatorga itarib yuborardi, kurs esa aynan tashqarida ishlayotgan │
   * │ sotuvchiga kerak — filial nomi esa unga emas: u O'Z filialida turadi   │
   * │ va uni almashtirmaydi (almashtirish rahbar ishi, u stol ustidagi       │
   * │ kompyuterda o'tiradi).                                                 │
   * │                                                                        │
   * │ Chegara `sm` (640) dan `md` (768) ga ko'tarildi: 640–767 oralig'ida    │
   * │ (planshet, kichik noutbuk) filial + kurs + til + foydalanuvchi bitta   │
   * │ qatorga sig'masdi va panel ikki qatorga o'ralardi.                     │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  /*
   * ┌────────────────────────────────────────────────────────────────────────┐
   * │ RANGLAR KORPUSNIKI — CHUNKI KO'RSATKICH YUQORI PANEL ICHIDA TURADI     │
   * │                                                                        │
   * │ `border-line bg-surface-2 text-ink` oq panel uchun tanlangan edi.      │
   * │ Panel chuqur jadega o'tgach chip jade ustidagi OQ QUTI bo'lib qoldi    │
   * │ va yonidagi kurs chipi bilan bir oilada ko'rinmasdi.                    │
   * │                                                                        │
   * │ `chrome-*` mavzuga bog'liq emas, shuning uchun bu yerda `dark:`        │
   * │ variantlari yo'q: korpus kunduzi ham, kechqurun ham bir xil yashil.    │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  if (!canSwitch) {
    return (
      <span
        className="hidden h-9 items-center gap-1.5 rounded-lg border border-chrome-line bg-chrome-2 px-2.5 md:inline-flex"
        title={branch?.name ?? ''}
      >
        <BranchIcon />
        <span className="max-w-[9rem] truncate text-sm font-medium text-chrome-ink">
          {branch?.name ?? branches[0]?.name}
        </span>
      </span>
    );
  }

  return (
    <label className="hidden h-9 items-center gap-1.5 rounded-lg border border-chrome-line bg-chrome-2 px-2 md:inline-flex">
      <BranchIcon />
      <span className="sr-only">{t('nav.branch')}</span>
      <select
        value={branchId === null ? '' : String(branchId)}
        onChange={(e) => setBranchId(e.target.value === '' ? null : Number(e.target.value))}
        className="max-w-[9rem] cursor-pointer truncate border-0 bg-transparent py-0 pr-1 text-sm font-medium text-chrome-ink focus:ring-0"
      >
        {/*
          «Barcha filiallar» — FAQAT almashtira oladigan foydalanuvchida.
          Rahbar uchun bu asosiy ko'rinish: u kompaniyani butun holda
          ko'radi va faqat kerak bo'lganda bitta filialga toraytiradi.

          ╔══════════════════════════════════════════════════════════════════╗
          ║ `<option>` LARGA RANG QO'LDA BERILADI — VA BU SHART              ║
          ║                                                                  ║
          ║ Yopiq holatdagi `<select>` panel ichida, ya'ni uning matni       ║
          ║ `text-chrome-ink` (deyarli oq). Lekin OCHILGAN ro'yxatni         ║
          ║ brauzerning O'ZI chizadi va u ish maydoni ustida suzadi.         ║
          ║ Chrome/Edge o'sha ro'yxatga `select` ning rangini meros qiladi,  ║
          ║ fonini esa `bg-transparent` dan ololmaydi va OQ qilib qo'yadi —  ║
          ║ natijada oq fonda oq matn, ya'ni filiallar ro'yxati BO'SH        ║
          ║ ko'rinardi.                                                       ║
          ║                                                                  ║
          ║ Shuning uchun ro'yxat qatorlari ataylab yorug' sirt tokenlarida  ║
          ║ qoladi: ular korpusning emas, ish maydonining qatlami.           ║
          ╚══════════════════════════════════════════════════════════════════╝
        */}
        <option value="" className="bg-surface text-ink">
          {t('nav.allBranches')}
        </option>
        {branches.map((b) => (
          <option key={b.id} value={b.id} className="bg-surface text-ink">
            {b.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function BranchIcon(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      /* Ikonka FAQAT yuqori paneldagi ko'rsatkich ichida chiziladi, ya'ni
         uning foni doim jade — rangi ham korpusniki. */
      className="size-4 shrink-0 text-chrome-muted"
      aria-hidden="true"
    >
      <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
